// src/communaute/ui.jsx
//
// Petits composants partagés : avatar, spinner, modale, toasts,
// confirmation, scroll infini. Thème : clair (fond blanc, texte sombre),
// accent violet Thrundrz.

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, User, X } from 'lucide-react';

export const btnPrimaire =
  'inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-[#7C3AED]/25 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50';

export const btnSecondaire =
  'inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:opacity-50';

export const champ =
  'w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none transition focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20';

// ══════════════════════════════════════════════════════
//  AVATAR
// ══════════════════════════════════════════════════════
export function Avatar({ src, taille = 44, icone: Icone = User, className = '' }) {
  const [erreur, setErreur] = useState(false);
  useEffect(() => setErreur(false), [src]);

  return (
    <div
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 ${className}`}
      style={{ width: taille, height: taille }}
    >
      {src && !erreur ? (
        <img src={src} alt="" className="h-full w-full object-cover" onError={() => setErreur(true)} />
      ) : (
        <Icone className="text-gray-400" size={Math.round(taille * 0.5)} />
      )}
    </div>
  );
}

export function Spinner({ taille = 26, className = '' }) {
  return <Loader2 className={`animate-spin text-[#7C3AED] ${className}`} size={taille} />;
}

export function Chargement() {
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
}

export function Vide({ icone: Icone, titre, sous }) {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      {Icone && <Icone className="mb-4 text-gray-300" size={52} />}
      <p className="text-[15px] text-gray-500">{titre}</p>
      {sous && <p className="mt-1.5 text-[13px] text-gray-400">{sous}</p>}
    </div>
  );
}

/** Pastille compteur (99+) */
export function Pastille({ n }) {
  if (!n) return null;
  return (
    <span className="ml-1.5 inline-flex min-w-[20px] items-center justify-center rounded-full bg-[#7C3AED] px-1.5 text-[11px] font-bold leading-5 text-white">
      {n > 99 ? '99+' : n}
    </span>
  );
}

// ══════════════════════════════════════════════════════
//  MODALE
// ══════════════════════════════════════════════════════
export function Modal({ ouvert, onFermer, titre, children, pied, taille = 'md' }) {
  useEffect(() => {
    if (!ouvert) return undefined;
    const echap = (e) => e.key === 'Escape' && onFermer?.();
    window.addEventListener('keydown', echap);
    const ancien = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', echap);
      document.body.style.overflow = ancien;
    };
  }, [ouvert, onFermer]);

  if (!ouvert) return null;

  const largeur = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' }[taille];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onFermer?.()}
    >
      <div
        className={`flex max-h-[92vh] w-full ${largeur} flex-col overflow-hidden rounded-t-2xl border border-gray-200 bg-white shadow-2xl sm:rounded-2xl`}
      >
        {titre !== undefined && (
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
            <h3 className="text-base font-bold text-gray-900">{titre}</h3>
            <button onClick={onFermer} className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-900">
              <X size={20} />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {pied && <div className="border-t border-gray-200 px-5 py-4">{pied}</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  TOASTS + CONFIRMATION (utilisables depuis n'importe où)
// ══════════════════════════════════════════════════════
const bus = new EventTarget();

export function toast(message, succes = false) {
  bus.dispatchEvent(new CustomEvent('toast', { detail: { message, succes, id: Date.now() + Math.random() } }));
}

/** await confirmer({ titre, message, bouton }) → true / false */
export function confirmer({ titre, message = '', bouton = 'Confirmer', danger = true }) {
  return new Promise((resolve) => {
    bus.dispatchEvent(new CustomEvent('confirm', { detail: { titre, message, bouton, danger, resolve } }));
  });
}

/** À placer une fois dans la page (déjà fait dans CommunautePage) */
export function UiHost() {
  const [toasts, setToasts] = useState([]);
  const [conf, setConf] = useState(null);

  useEffect(() => {
    const onToast = (e) => {
      const t = e.detail;
      setToasts((l) => [...l, t]);
      setTimeout(() => setToasts((l) => l.filter((x) => x.id !== t.id)), 3200);
    };
    const onConf = (e) => setConf(e.detail);
    bus.addEventListener('toast', onToast);
    bus.addEventListener('confirm', onConf);
    return () => {
      bus.removeEventListener('toast', onToast);
      bus.removeEventListener('confirm', onConf);
    };
  }, []);

  const repondre = (v) => {
    conf?.resolve(v);
    setConf(null);
  };

  return (
    <>
      <div className="pointer-events-none fixed bottom-6 left-1/2 z-[60] flex -translate-x-1/2 flex-col items-center gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white shadow-xl ${t.succes ? 'bg-[#258561]' : 'bg-[#3a1d4d]'
              }`}
          >
            {t.succes ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
            {t.message}
          </div>
        ))}
      </div>

      <Modal ouvert={!!conf} onFermer={() => repondre(false)} titre={conf?.titre} taille="sm">
        {conf?.message && <p className="px-5 py-4 text-sm text-gray-600">{conf.message}</p>}
        <div className="flex justify-end gap-2 px-5 pb-5 pt-2">
          <button className={btnSecondaire} onClick={() => repondre(false)}>
            Annuler
          </button>
          <button
            className={
              conf?.danger
                ? 'rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-500'
                : btnPrimaire
            }
            onClick={() => repondre(true)}
          >
            {conf?.bouton}
          </button>
        </div>
      </Modal>
    </>
  );
}

// ══════════════════════════════════════════════════════
//  SCROLL INFINI — renvoie une ref à poser sur un élément
//  en bas de liste ; onFin() est appelé quand il devient visible
// ══════════════════════════════════════════════════════
export function useScrollInfini(onFin, actif) {
  const ref = useRef(null);
  const cb = useRef(onFin);
  cb.current = onFin;

  useEffect(() => {
    if (!actif || !ref.current) return undefined;
    const obs = new IntersectionObserver((e) => e[0].isIntersecting && cb.current(), { rootMargin: '300px' });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [actif]);

  return ref;
}